import { useEffect, useRef, useState } from "react";
import { MENU_CATEGORIES } from "@/data/menu";
import { UBER_EATS_URL } from "@/config/site";
import { ChevronRight } from "lucide-react";

function MenuCard({ item }) {
  return (
    <article
      data-testid={`menu-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="group bg-brand-light rounded-2xl overflow-hidden shadow-lg flex flex-col h-full transform transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_25px_50px_-12px_rgba(255,102,0,0.25)]"
    >
      <div className="relative w-full aspect-[4/3] bg-gray-200 overflow-hidden">
        <img
          src={item.image}
          alt={item.name}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute top-3 right-3 bg-[#0A0A0A] text-brand-orange font-subheading font-black text-base px-3 py-1.5 rounded-full shadow-md">
          £{item.price}
        </div>
      </div>
      <div className="p-5 sm:p-6 flex flex-col flex-grow">
        <h3 className="font-heading text-2xl sm:text-3xl text-[#0A0A0A] uppercase tracking-tight leading-none mb-2">
          {item.name}
        </h3>
        <p className="text-[#4B5563] text-sm font-body leading-relaxed flex-grow">
          {item.desc}
        </p>
      </div>
    </article>
  );
}

export default function Menu() {
  const [activeCat, setActiveCat] = useState(MENU_CATEGORIES[0].id);
  const sectionRefs = useRef({});

  useEffect(() => {
    // Observe each category section to update the active pill
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveCat(entry.target.id);
        });
      },
      { rootMargin: "-160px 0px -55% 0px", threshold: 0 }
    );
    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="relative bg-[#0A0A0A] bg-grain py-16 sm:py-24"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-14">
          <span className="inline-block font-subheading font-bold uppercase tracking-[0.3em] text-brand-orange text-xs mb-4">
            The Menu
          </span>
          <h2 className="font-heading text-5xl sm:text-7xl lg:text-8xl text-white uppercase tracking-tight leading-none">
            Built fresh. <br />
            <span className="text-brand-green">Smashed</span> to order.
          </h2>
          <p className="mt-5 font-body text-white/60 max-w-2xl mx-auto text-base sm:text-lg">
            Every burger hand-formed, every bao steamed in-house, every wing fried to order.
          </p>
        </div>
      </div>

      {/* Sticky category pill nav */}
      <div
        data-testid="menu-category-nav"
        className="sticky top-16 sm:top-20 z-30 bg-[#0A0A0A]/95 backdrop-blur-md border-y border-white/10 mb-10 sm:mb-14"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex gap-2 sm:gap-3 overflow-x-auto hide-scrollbar py-3 sm:py-4">
            {MENU_CATEGORIES.map((cat) => (
              <a
                key={cat.id}
                href={`#${cat.id}`}
                data-testid={`menu-cat-pill-${cat.id}`}
                className={`whitespace-nowrap font-subheading font-bold uppercase tracking-wider text-xs sm:text-sm px-4 sm:px-5 py-2 sm:py-2.5 rounded-full transition-all border ${
                  activeCat === cat.id
                    ? "bg-brand-orange text-white border-brand-orange shadow-[0_4px_20px_0_rgba(255,102,0,0.4)]"
                    : "bg-transparent text-white/70 border-white/15 hover:text-white hover:border-white/40"
                }`}
              >
                {cat.name}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Category sections */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20 sm:space-y-28">
        {MENU_CATEGORIES.map((cat) => (
          <section
            key={cat.id}
            id={cat.id}
            ref={(el) => (sectionRefs.current[cat.id] = el)}
            data-testid={`menu-category-${cat.id}`}
            className="scroll-mt-40"
          >
            <div className="flex items-end justify-between mb-6 sm:mb-10 gap-4">
              <div>
                <span className="font-subheading font-bold uppercase tracking-[0.25em] text-brand-orange text-xs sm:text-sm">
                  Category
                </span>
                <h3 className="font-heading text-4xl sm:text-6xl lg:text-7xl text-white uppercase tracking-tight leading-none mt-2">
                  {cat.name}
                </h3>
                <p className="mt-3 text-white/60 font-body text-sm sm:text-base max-w-xl">
                  {cat.tagline}
                </p>
              </div>
              <a
                href={UBER_EATS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`menu-cat-order-${cat.id}`}
                className="hidden sm:inline-flex items-center gap-2 font-subheading font-bold uppercase tracking-widest text-xs text-brand-orange hover:text-white transition-colors"
              >
                Order Now <ChevronRight size={16} />
              </a>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-7">
              {cat.items.map((item) => (
                <MenuCard key={item.name} item={item} />
              ))}
            </div>
          </section>
        ))}
      </div>

      {/* Bottom CTA */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20 sm:mt-28">
        <div className="bg-brand-orange rounded-3xl px-6 sm:px-12 py-10 sm:py-14 text-center sm:text-left flex flex-col sm:flex-row items-center justify-between gap-6 shadow-[0_20px_60px_-15px_rgba(255,102,0,0.5)]">
          <div>
            <h3 className="font-heading text-4xl sm:text-6xl text-black uppercase leading-none tracking-tight">
              Hungry yet?
            </h3>
            <p className="font-subheading font-semibold uppercase tracking-widest text-black/70 text-xs sm:text-sm mt-3">
              Order in seconds. Delivered hot.
            </p>
          </div>
          <a
            href={UBER_EATS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="menu-bottom-cta"
            className="inline-flex items-center gap-3 bg-black text-white font-subheading font-bold uppercase tracking-widest text-sm sm:text-base px-8 py-4 sm:py-5 rounded-full hover:bg-brand-bg2 transition-all hover:-translate-y-1"
          >
            Order on Uber Eats <ChevronRight size={18} />
          </a>
        </div>
      </div>
    </section>
  );
}
